import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate, getStatusColor, CURRENCIES } from '../lib/utils';
import { getApiErrorMessage, isEmailNotVerifiedError } from '../lib/apiError';
import { analytics } from '../lib/analytics';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Send, 
  Download, 
  Edit, 
  Copy, 
  ExternalLink, 
  Loader2, 
  FileText,
  DollarSign,
  Bell,
  CheckCircle,
  Clock,
  Trash2,
  Repeat,
  Share2,
  MessageSquare,
  RefreshCw,
  AlertTriangle,
  History,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const InvoiceDetail = () => {
  const { invoiceId } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [resendingInvoice, setResendingInvoice] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  // Payment dialog state
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [savingPayment, setSavingPayment] = useState(false);

  useEffect(() => {
    fetchInvoice();
  }, [invoiceId]);

  const fetchInvoice = async () => {
    try {
      const response = await api.get(`/invoices/${invoiceId}`);
      setInvoice(response.data);
    } catch (error) {
      toast.error('Invoice not found');
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };

  const notifyVerifyEmail = () => {
    toast.error('Verify your email before sending invoices. Check your inbox for the verification link.', {
      action: {
        label: 'Resend email',
        onClick: async () => {
          try {
            await api.post('/auth/resend-verification');
            toast.success('Verification email sent — please check your inbox.');
          } catch (err) {
            toast.error(getApiErrorMessage(err, 'Could not send verification email.'));
          }
        },
      },
      duration: 8000,
    });
  };

  const handleSendInvoice = async () => {
    // Check if client has email
    if (!invoice?.client_email) {
      toast.error('This client doesn\'t have an email address. Use "Share Payment Link" instead.');
      return;
    }
    
    setSendingInvoice(true);
    try {
      await api.post(`/invoices/${invoiceId}/send`, { origin_url: window.location.origin });
      analytics.invoiceSent();
      toast.success('Invoice sent successfully! Email notification sent to client.');
      fetchInvoice();
    } catch (error) {
      if (isEmailNotVerifiedError(error)) {
        notifyVerifyEmail();
      } else if (error.response?.status === 400 && error.response?.headers?.['x-error-code'] === 'no_client_email') {
        toast.error('This client doesn\'t have an email address. Use "Share Payment Link" instead.');
      } else {
        toast.error(getApiErrorMessage(error, 'Failed to send invoice'));
      }
    } finally {
      setSendingInvoice(false);
    }
  };

  const handleSharePaymentLink = async () => {
    const url = `${window.location.origin}/pay/${invoiceId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Payment link copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleShareViaSMS = () => {
    const url = `${window.location.origin}/pay/${invoiceId}`;
    const clientPhone = invoice?.client_phone || '';
    const businessName = invoice?.freelancer_name || 'Your business';
    const amount = formatCurrency(invoice?.total || 0, invoice?.currency || 'USD');
    
    const message = `Hi! Your invoice (${invoice?.invoice_number}) for ${amount} is ready. Pay here: ${url}`;
    
    // Open SMS app with prefilled message
    const smsUrl = `sms:${clientPhone}?body=${encodeURIComponent(message)}`;
    window.open(smsUrl, '_blank');
  };

  const handleMarkAsShared = async () => {
    try {
      await api.post(`/invoices/${invoiceId}/mark-shared`);
      toast.success('Invoice marked as sent');
      fetchInvoice();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to mark invoice as sent'));
    }
  };

  const handleResendInvoice = async () => {
    if (!invoice?.client_email) {
      handleSharePaymentLink();
      return;
    }
    
    setResendingInvoice(true);
    try {
      const response = await api.post(`/invoices/${invoiceId}/resend`, { origin_url: window.location.origin });
      if (response.data.is_amendment) {
        toast.success('Updated invoice sent to client');
      } else {
        toast.success('Invoice reminder sent to client');
      }
      fetchInvoice();
    } catch (error) {
      if (isEmailNotVerifiedError(error)) {
        notifyVerifyEmail();
      } else if (error.response?.data?.code === 'no_client_email') {
        handleSharePaymentLink();
      } else {
        toast.error(getApiErrorMessage(error, 'Failed to resend invoice'));
      }
    } finally {
      setResendingInvoice(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloadingPDF(true);
    try {
      const response = await api.get(`/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const handleSendReminder = async () => {
    try {
      await api.post(`/invoices/${invoiceId}/remind`, { origin_url: window.location.origin });
      toast.success('Payment reminder sent to client!');
    } catch (error) {
      if (isEmailNotVerifiedError(error)) {
        notifyVerifyEmail();
      } else {
        toast.error(getApiErrorMessage(error, 'Failed to send reminder'));
      }
    }
  };

  const handleMarkPaid = async () => {
    try {
      await api.put(`/invoices/${invoiceId}`, { status: 'paid' });
      toast.success('Invoice marked as paid');
      fetchInvoice();
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  const handleDuplicate = async () => {
    try {
      const response = await api.post(`/invoices/${invoiceId}/duplicate`);
      toast.success(`Invoice duplicated as ${response.data.invoice_number}`);
      navigate(`/invoices/${response.data.invoice_id}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to duplicate invoice'));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await api.delete(`/invoices/${invoiceId}`);
      toast.success('Invoice deleted');
      navigate('/invoices');
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const copyPaymentLink = () => {
    const url = `${window.location.origin}/pay/${invoiceId}`;
    navigator.clipboard.writeText(url);
    toast.success('Payment link copied to clipboard');
  };

  const openPaymentDialog = () => {
    setPaymentAmount((invoice.balance_due || invoice.total).toString());
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentMethod('');
    setPaymentNotes('');
    setShowPaymentDialog(true);
  };

  const handleRecordPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    setSavingPayment(true);
    try {
      await api.post(`/invoices/${invoiceId}/payments`, {
        amount: parseFloat(paymentAmount),
        payment_date: paymentDate,
        payment_method: paymentMethod || null,
        notes: paymentNotes || null
      });
      toast.success('Payment recorded successfully');
      setShowPaymentDialog(false);
      fetchInvoice();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to record payment'));
    } finally {
      setSavingPayment(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
        </div>
      </Layout>
    );
  }

  if (!invoice) return null;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      case 'partially_paid':
        return <DollarSign className="w-5 h-5 text-blue-500" />;
      case 'sent':
        return <Clock className="w-5 h-5 text-amber-500" />;
      default:
        return <FileText className="w-5 h-5 text-muted-foreground" />;
    }
  };

  return (
    <Layout>
      <div className="p-6 lg:p-12 max-w-5xl mx-auto space-y-8" data-testid="invoice-detail-page">
        <EmailVerificationBanner />
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/invoices')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="font-heading font-bold text-3xl tracking-tight">{invoice.invoice_number}</h1>
                <Badge variant="secondary" className={`text-sm ${getStatusColor(invoice.status)}`}>
                  {invoice.status.replace('_', ' ')}
                </Badge>
                {(() => {
                  const today = new Date().toISOString().split('T')[0];
                  const isOverdue = invoice.status !== 'paid' && invoice.status !== 'draft' && invoice.due_date < today;
                  return isOverdue ? <Badge variant="destructive" className="text-sm">OVERDUE</Badge> : null;
                })()}
              </div>
              <p className="text-muted-foreground mt-1">
                Created {formatDate(invoice.created_at)}
                {invoice.version > 1 && (
                  <span className="ml-2">
                    <Badge variant="outline" className="text-xs">v{invoice.version}</Badge>
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Version Banner - shown when invoice has been amended */}
        {invoice.version > 1 && (
          <div className="flex items-center gap-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-amber-700">
                This invoice has been updated
              </p>
              <p className="text-sm text-amber-600/80">
                Version {invoice.version} • Last amended {formatDate(invoice.amended_at)}
              </p>
            </div>
            {invoice.prior_versions?.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowVersionHistory(!showVersionHistory)}
                className="gap-2"
              >
                <History className="w-4 h-4" />
                {showVersionHistory ? 'Hide' : 'View'} History
                {showVersionHistory ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </Button>
            )}
          </div>
        )}

        {/* Version History */}
        {showVersionHistory && invoice.prior_versions?.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="w-5 h-5" />
                Version History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {invoice.prior_versions.slice().reverse().map((v, idx) => (
                  <div key={idx} className="p-3 bg-muted/50 rounded-lg text-sm">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Version {v.version}</span>
                      <span className="text-muted-foreground text-xs">{formatDate(v.amended_at)}</span>
                    </div>
                    <div className="text-muted-foreground">
                      Total: {formatCurrency(v.total, v.currency || 'USD')}
                      {v.balance_due !== v.total && ` • Balance: ${formatCurrency(v.balance_due, v.currency || 'USD')}`}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">Quick Actions</CardTitle>
            <CardDescription>Send, download, or manage this invoice</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {invoice.status === 'draft' && (
                <>
                  {invoice.client_email ? (
                    <Button
                      onClick={handleSendInvoice}
                      disabled={sendingInvoice || !user?.email_verified}
                      title={!user?.email_verified ? 'Verify your email to unlock sending.' : undefined}
                      className="gap-2"
                      data-testid="send-invoice-btn"
                    >
                      {sendingInvoice ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Send to Client
                    </Button>
                  ) : (
                    <>
                      <Button onClick={handleSharePaymentLink} className="gap-2" data-testid="share-payment-link-btn">
                        <Share2 className="w-4 h-4" />
                        Copy Payment Link
                      </Button>
                      <Button variant="outline" onClick={handleShareViaSMS} className="gap-2" data-testid="share-via-sms-btn">
                        <MessageSquare className="w-4 h-4" />
                        Share via SMS
                      </Button>
                      <Button variant="ghost" onClick={handleMarkAsShared} className="gap-2 text-sm" data-testid="mark-shared-btn">
                        <CheckCircle className="w-4 h-4" />
                        Mark as Sent
                      </Button>
                    </>
                  )}
                  <Link to={`/invoices/${invoiceId}/edit`}>
                    <Button variant="outline" className="gap-2" data-testid="edit-invoice-btn">
                      <Edit className="w-4 h-4" />
                      Edit Invoice
                    </Button>
                  </Link>
                </>
              )}
              
              {(invoice.status === 'sent' || invoice.status === 'partially_paid') && (
                <>
                  <Link to={`/invoices/${invoiceId}/edit`}>
                    <Button variant="outline" className="gap-2" data-testid="edit-invoice-btn">
                      <Edit className="w-4 h-4" />
                      Edit Invoice
                    </Button>
                  </Link>
                  <Button onClick={openPaymentDialog} className="gap-2" data-testid="record-payment-btn">
                    <DollarSign className="w-4 h-4" />
                    Record Payment
                  </Button>
                  {invoice.client_email ? (
                    <>
                      <Button
                        variant="outline"
                        onClick={handleSendReminder}
                        disabled={!user?.email_verified}
                        title={!user?.email_verified ? 'Verify your email to unlock sending.' : undefined}
                        className="gap-2"
                        data-testid="send-reminder-btn"
                      >
                        <Bell className="w-4 h-4" />
                        Send Reminder
                      </Button>
                      {invoice.version > 1 && (
                        <Button 
                          variant="outline" 
                          onClick={handleResendInvoice} 
                          disabled={resendingInvoice || !user?.email_verified}
                          title={!user?.email_verified ? 'Verify your email to unlock sending.' : undefined}
                          className="gap-2" 
                          data-testid="resend-invoice-btn"
                        >
                          {resendingInvoice ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                          Resend Updated Invoice
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <Button variant="outline" onClick={handleSharePaymentLink} className="gap-2" data-testid="share-payment-link-btn">
                        <Share2 className="w-4 h-4" />
                        Copy Payment Link
                      </Button>
                      <Button variant="ghost" onClick={handleShareViaSMS} className="gap-2" data-testid="share-via-sms-btn">
                        <MessageSquare className="w-4 h-4" />
                        Share via SMS
                      </Button>
                    </>
                  )}
                  <Button variant="outline" onClick={handleMarkPaid} className="gap-2" data-testid="mark-paid-btn">
                    <CheckCircle className="w-4 h-4" />
                    Mark as Paid
                  </Button>
                </>
              )}

              <Button variant="outline" onClick={handleDownloadPDF} disabled={downloadingPDF} className="gap-2" data-testid="download-pdf-btn">
                {downloadingPDF ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Download PDF
              </Button>
              
              <Button variant="outline" onClick={copyPaymentLink} className="gap-2" data-testid="copy-link-btn">
                <Copy className="w-4 h-4" />
                Copy Payment Link
              </Button>

              <Button variant="outline" onClick={() => window.open(`/pay/${invoiceId}`, '_blank')} className="gap-2" data-testid="view-public-btn">
                <ExternalLink className="w-4 h-4" />
                View Public Page
              </Button>

              <Button variant="outline" onClick={handleDuplicate} className="gap-2" data-testid="duplicate-btn">
                <FileText className="w-4 h-4" />
                Duplicate
              </Button>

              <Button variant="outline" onClick={handleDelete} className="gap-2 text-destructive hover:text-destructive" data-testid="delete-btn">
                <Trash2 className="w-4 h-4" />
                Delete
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Invoice Details */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Invoice Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Client Info */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Bill To</p>
                  <p className="font-medium">{invoice.client_name}</p>
                  <p className="text-sm text-muted-foreground">{invoice.client_email}</p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Due Date</p>
                  <p className="font-medium">{formatDate(invoice.due_date)}</p>
                </div>
              </div>

              {/* Line Items */}
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">Items</p>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium">Description</th>
                        <th className="text-right p-3 text-sm font-medium">Qty</th>
                        <th className="text-right p-3 text-sm font-medium">Price</th>
                        <th className="text-right p-3 text-sm font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.items.map((item, index) => (
                        <tr key={index} className="border-t">
                          <td className="p-3">{item.description}</td>
                          <td className="p-3 text-right font-mono">{item.quantity}</td>
                          <td className="p-3 text-right font-mono">{formatCurrency(item.unit_price, invoice.currency)}</td>
                          <td className="p-3 text-right font-mono font-medium">
                            {formatCurrency(item.quantity * item.unit_price, invoice.currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Notes */}
              {invoice.notes && (
                <div>
                  <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Notes</p>
                  <p className="text-muted-foreground">{invoice.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Summary Card */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Summary</span>
                  {invoice.currency && invoice.currency !== 'USD' && (
                    <Badge variant="outline" className="font-mono">{invoice.currency}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-mono">{formatCurrency(invoice.subtotal, invoice.currency)}</span>
                </div>
                
                {invoice.tax_rate > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Tax ({invoice.tax_rate}%)</span>
                    <span className="font-mono">{formatCurrency(invoice.tax_amount || 0, invoice.currency)}</span>
                  </div>
                )}
                
                <div className="flex justify-between font-bold border-t pt-4">
                  <span>Total</span>
                  <span className="font-mono text-lg">{formatCurrency(invoice.total, invoice.currency)}</span>
                </div>

                {(invoice.amount_paid > 0 || invoice.status === 'partially_paid') && (
                  <>
                    <div className="flex justify-between text-sm text-emerald-600 pt-2 border-t">
                      <span>Amount Paid</span>
                      <span className="font-mono">-{formatCurrency(invoice.amount_paid || 0, invoice.currency)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-primary">
                      <span>Balance Due</span>
                      <span className="font-mono text-lg">{formatCurrency(invoice.balance_due || invoice.total, invoice.currency)}</span>
                    </div>
                  </>
                )}

                {/* Late Fee Display */}
                {invoice.late_fee_accrued > 0 && (
                  <div className="flex justify-between text-sm text-destructive pt-2 border-t">
                    <span>Late Fee Accrued</span>
                    <span className="font-mono font-medium">+{formatCurrency(invoice.late_fee_accrued, invoice.currency)}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Status Card */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  {getStatusIcon(invoice.status)}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium capitalize">{invoice.status.replace('_', ' ')}</p>
                      {(() => {
                        const today = new Date().toISOString().split('T')[0];
                        const isOverdue = invoice.status !== 'paid' && invoice.status !== 'draft' && invoice.due_date < today;
                        return isOverdue ? <Badge variant="destructive" className="text-xs">OVERDUE</Badge> : null;
                      })()}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {invoice.status === 'draft' && 'Ready to send'}
                      {invoice.status === 'sent' && 'Awaiting payment'}
                      {invoice.status === 'partially_paid' && `${formatCurrency(invoice.balance_due, invoice.currency)} remaining`}
                      {invoice.status === 'paid' && `Paid on ${formatDate(invoice.paid_at)}`}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment History */}
            {invoice.payments && invoice.payments.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Payment History</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {invoice.payments.map((payment, index) => (
                    <div key={index} className="flex justify-between items-center text-sm p-3 bg-muted rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium">{formatCurrency(payment.amount, invoice.currency)}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(payment.date || payment.payment_date)} • {payment.method || payment.payment_method || 'Manual'}
                        </p>
                        {/* Show fee breakdown for Stripe payments */}
                        {payment.method === 'stripe' && payment.platform_fee > 0 && (
                          <p className="text-xs text-muted-foreground mt-1" data-testid={`payment-fee-breakdown-${index}`}>
                            {payment.net_to_freelancer_is_estimate === false && payment.stripe_fee != null ? (
                              <>
                                Stripe fee: {formatCurrency(payment.stripe_fee, invoice.currency)} • Platform fee: {formatCurrency(payment.platform_fee, invoice.currency)} • Net deposited: {formatCurrency(payment.net_to_freelancer, invoice.currency)}
                              </>
                            ) : (
                              <>
                                Platform fee: {formatCurrency(payment.platform_fee, invoice.currency)} • Est. net payout: {formatCurrency(payment.net_to_freelancer, invoice.currency)} (excludes Stripe's processing fee)
                              </>
                            )}
                          </p>
                        )}
                      </div>
                      <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Record Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              Record a payment for invoice {invoice.invoice_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-muted p-4 rounded-lg space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Invoice Total:</span>
                <span className="font-medium">{formatCurrency(invoice.total, invoice.currency)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Amount Paid:</span>
                <span className="font-medium">{formatCurrency(invoice.amount_paid || 0, invoice.currency)}</span>
              </div>
              <div className="flex justify-between font-medium border-t pt-1 mt-1">
                <span>Balance Due:</span>
                <span>{formatCurrency(invoice.balance_due || invoice.total, invoice.currency)}</span>
              </div>
            </div>

            <div>
              <Label htmlFor="payment-amount">Payment Amount ({invoice.currency}) *</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">{CURRENCIES[invoice.currency]?.symbol || '$'}</span>
                <Input
                  id="payment-amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  max={invoice.balance_due || invoice.total}
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="pl-7"
                  data-testid="payment-amount-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="payment-date">Payment Date *</Label>
              <Input
                id="payment-date"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="mt-1.5"
                data-testid="payment-date-input"
              />
            </div>

            <div>
              <Label htmlFor="payment-method">Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="mt-1.5" data-testid="payment-method-select">
                  <SelectValue placeholder="Select method (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="stripe">Stripe</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="payment-notes">Notes (optional)</Label>
              <Input
                id="payment-notes"
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="e.g., Reference number, memo"
                className="mt-1.5"
                data-testid="payment-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowPaymentDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleRecordPayment} disabled={savingPayment} data-testid="save-payment-btn">
              {savingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default InvoiceDetail;
